import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import type { Workflow } from '@fv/models'

import {
  useAreDimsRequired,
  useIsNmfcRequired,
} from '@/features/settings/account-settings/hooks'

import { HandlingUnitContainsPanel } from './HandlingUnitContainsPanel'
import { InlineItemsFormWrapper } from './InlineItemsForm'
import {
  LoadItemsContext,
  useItemActions,
  useItemsState,
} from './loadItemsContext'
import {
  createItemsStore,
  type LoadItemsStore,
  type LoadItemsStoreInit,
} from './loadItemsFormState'
import { PagedItemsFormWrapper } from './PagedItemsForm'
import type { LoadHandlingUnitFormModel, LoadStep } from './types'

export type LoadItemsFormRef = {
  validateForm: () => boolean
  validateItems: (workflow: Workflow, step: LoadStep) => boolean
  setItems: (items: LoadHandlingUnitFormModel[]) => void
}
type Props = Omit<LoadItemsStoreInit, 'items'> & {
  type: 'inline' | 'paged'
  onChange: (items: LoadHandlingUnitFormModel[]) => void
  initialItems?: LoadHandlingUnitFormModel[]
  onSubmit?: () => void
}
export const LoadItemsForm = forwardRef<LoadItemsFormRef, Props>(
  (
    {
      workflow,
      step,
      type,
      onChange,
      initialItems,
      onSubmit,
      stops,
      isTemplate,
    }: Props,
    ref,
  ) => {
    const requireDims = useAreDimsRequired(workflow)
    const requireNmfc = useIsNmfcRequired(workflow)
    const storeRef = useRef<LoadItemsStore>(null)
    const isRefItemsUpdate = useRef(false)
    if (!storeRef.current) {
      storeRef.current = createItemsStore({
        workflow,
        items: initialItems,
        step,
        stops,
        isTemplate,
      })
    }

    const updateStoreItems = useCallback(
      (items: LoadHandlingUnitFormModel[]) => {
        isRefItemsUpdate.current = true
        storeRef.current.getState().actions.setItems(items)
        setTimeout(() => {
          isRefItemsUpdate.current = false
        }, 0)
      },
      [],
    )

    // Expose the validateForm method to the parent component
    useImperativeHandle(ref, () => ({
      validateForm: () => {
        return storeRef.current.getState().actions.validateForm()
      },
      validateItems: (workflow: Workflow, step: LoadStep) => {
        return storeRef.current
          .getState()
          .actions.validateItems(workflow, step, requireDims, requireNmfc)
      },
      setItems: updateStoreItems,
    }))

    useEffect(() => {
      const currentWorkflow = storeRef.current.getState().workflow

      storeRef.current.setState(p => ({
        ...p,
        step,
        workflow,
      }))

      // Reset items if workflow has changed
      if (initialItems && workflow !== currentWorkflow) {
        updateStoreItems(initialItems)
      }
    }, [step, workflow, initialItems, updateStoreItems])

    useEffect(() => {
      if (storeRef.current.getState().stops !== stops) {
        storeRef.current.setState({ stops })
      }
    }, [stops])

    useEffect(() => {
      const unsub = storeRef.current.subscribe(({ items }) => {
        !isRefItemsUpdate.current && onChange(items)
      })
      return () => {
        unsub()
      }
    }, [onChange])
    return (
      <LoadItemsContext.Provider value={storeRef.current}>
        <LoadItemsFormProviderContents type={type} onSubmit={onSubmit} />
      </LoadItemsContext.Provider>
    )
  },
)

const LoadItemsFormProviderContents = ({
  type,
  onSubmit,
}: Pick<Props, 'type' | 'onSubmit'>) => {
  const { editingContainsUnit, items } = useItemsState()
  const { setEditingContains, updateItem, activateContains } = useItemActions()

  const handleContainsClose = (fromSave: boolean) => {
    if (
      fromSave ||
      window.confirm(
        `You have not saved your changes.  Are you sure you're finished editing commodities?`,
      )
    ) {
      setEditingContains(undefined)
      activateContains(undefined)
    }
  }

  return (
    <>
      {type === 'inline' ? (
        <InlineItemsFormWrapper />
      ) : (
        <PagedItemsFormWrapper onSubmit={onSubmit} />
      )}

      <HandlingUnitContainsPanel
        isOpen={!!editingContainsUnit}
        onClose={() => handleContainsClose(false)}
        handlingUnit={editingContainsUnit}
        onSubmit={({ contains }) => {
          if (!editingContainsUnit) return
          const ix = items.indexOf(editingContainsUnit)
          updateItem(ix, { contains })
          handleContainsClose(true)
        }}
      />
    </>
  )
}
